import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';

export default function AdminAdvisors() {
  const { api } = useAuth();
  const [advisors, setAdvisors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [showAddLeadsModal, setShowAddLeadsModal] = useState(false);
  const [showDeductLeadModal, setShowDeductLeadModal] = useState(false);
  const [showEditSettingsModal, setShowEditSettingsModal] = useState(false);
  const [addLeadsData, setAddLeadsData] = useState({ leads: 1 });
  const [deductReason, setDeductReason] = useState('');
  const [editSettingsData, setEditSettingsData] = useState({ leadsPerWeek: 2, requiresPhone: false, notificationEmail: '', weekdaysOnly: false, leadsPerDay: '' });
  const [newDestination, setNewDestination] = useState('');

  useEffect(() => {
    loadAdvisors();
  }, []);

  const loadAdvisors = async () => {
    try {
      const data = await api('/admin/advisors');
      // Sort: active advisors first, then by balance descending
      const sorted = data.advisors.sort((a, b) => {
        if (a.isActive !== b.isActive) return b.isActive - a.isActive;
        return b.balanceLeads - a.balanceLeads;
      });
      setAdvisors(sorted);
    } catch (error) {
      console.error('Failed to load advisors:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleActive = async (advisorId) => {
    try {
      await api(`/admin/advisors/${advisorId}/toggle-active`, { method: 'POST' });
      loadAdvisors();
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  const addLeads = async () => {
    if (!selectedAdvisor) return;
    
    try {
      await api(`/admin/advisors/${selectedAdvisor.id}/add-leads`, {
        method: 'POST',
        body: JSON.stringify(addLeadsData)
      });
      setShowAddLeadsModal(false);
      setAddLeadsData({ leads: 1 });
      loadAdvisors();
    } catch (error) {
      console.error('Failed to add leads:', error);
    }
  };

  const deductLead = async () => {
    if (!selectedAdvisor) return;
    
    try {
      await api(`/admin/advisors/${selectedAdvisor.id}/deduct-lead`, {
        method: 'POST',
        body: JSON.stringify({ reason: deductReason })
      });
      setShowDeductLeadModal(false);
      setDeductReason('');
      setSelectedAdvisor(null);
      loadAdvisors();
    } catch (error) {
      console.error('Failed to deduct lead:', error);
      alert(error.message || 'Failed to deduct lead');
    }
  };

  const updateSettings = async () => {
    if (!selectedAdvisor) return;
    
    try {
      await api(`/admin/advisors/${selectedAdvisor.id}/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          leads_per_week: editSettingsData.leadsPerWeek,
          requires_phone: editSettingsData.requiresPhone,
          notification_email: editSettingsData.notificationEmail,
          weekdays_only: editSettingsData.weekdaysOnly,
          leads_per_day: editSettingsData.leadsPerDay ? parseInt(editSettingsData.leadsPerDay) : null
        })
      });
      setShowEditSettingsModal(false);
      loadAdvisors();
    } catch (error) {
      console.error('Failed to update settings:', error);
      alert(error.message || 'Failed to update settings');
    }
  };

  const openEditSettings = (advisor) => {
    setSelectedAdvisor(advisor);
    setEditSettingsData({
      leadsPerWeek: typeof advisor.leadsPerWeek === 'number' ? advisor.leadsPerWeek : 2,
      requiresPhone: advisor.requiresPhone || false,
      notificationEmail: advisor.notificationEmail || '',
      weekdaysOnly: advisor.weekdaysOnly || false,
      leadsPerDay: advisor.leadsPerDay || ''
    });
    setNewDestination('');
    setShowEditSettingsModal(true);
  };

  const addDestination = async () => {
    if (!selectedAdvisor || !newDestination.trim()) return;
    
    try {
      await api(`/admin/advisors/${selectedAdvisor.id}/destinations`, {
        method: 'POST',
        body: JSON.stringify({ destination: newDestination.trim() })
      });
      setNewDestination('');
      loadAdvisors();
      // Update the selected advisor's destinations locally
      setSelectedAdvisor(prev => ({
        ...prev,
        destinations: [...(prev.destinations || []), newDestination.trim().toLowerCase()]
      }));
    } catch (error) {
      console.error('Failed to add destination:', error);
      alert(error.message || 'Failed to add destination');
    }
  };

  const removeDestination = async (destination) => {
    if (!selectedAdvisor) return;
    
    try {
      await api(`/admin/advisors/${selectedAdvisor.id}/destinations/${encodeURIComponent(destination)}`, {
        method: 'DELETE'
      });
      loadAdvisors();
      setSelectedAdvisor(prev => ({
        ...prev,
        destinations: (prev.destinations || []).filter(d => d !== destination)
      }));
    } catch (error) {
      console.error('Failed to remove destination:', error);
      alert(error.message || 'Failed to remove destination');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-coral-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl text-navy-900 mb-2">Manage Advisors</h1>
          <p className="text-navy-600 font-body">{advisors.length} registered advisors • Sorted by balance</p>
        </div>
        <a
          href="/api/admin/advisors/export/csv"
          download
          className="inline-flex items-center gap-2 px-4 py-2 bg-navy-700 hover:bg-navy-800 text-white text-sm font-medium rounded transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export CSV
        </a>
      </div>

      {/* Advisors Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-cream-50 border-b border-cream-200">
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Advisor</th>
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Balance</th>
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Leads/Week</th>
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Timeline</th>
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Weight</th>
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Received</th>
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Status</th>
                <th className="text-right text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {advisors.map((advisor) => (
                <tr key={advisor.id} className="border-b border-cream-100 hover:bg-cream-50">
                  <td className="py-4 px-4">
                    <div>
                      <p className="font-medium text-navy-900">{advisor.firstName} {advisor.lastName}</p>
                      <p className="text-sm text-navy-500 font-body">{advisor.email}</p>
                      {advisor.requiresPhone && (
                        <span className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          Phone required
                        </span>
                      )}
                      {advisor.weekdaysOnly && (
                        <span className="inline-flex items-center gap-1 mt-1 text-xs text-purple-600">
                          📅 Weekdays only
                        </span>
                      )}
                      {advisor.leadsPerDay && (
                        <span className="inline-flex items-center gap-1 mt-1 text-xs text-orange-600">
                          ⭐ {advisor.leadsPerDay}/day priority
                        </span>
                      )}
                      {advisor.notificationEmail && (
                        <span className="inline-flex items-center gap-1 mt-1 text-xs text-green-600">
                          ✉️ {advisor.notificationEmail}
                        </span>
                      )}
                      {advisor.destinations && advisor.destinations.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {advisor.destinations.map(dest => (
                            <span key={dest} className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                              🌍 {dest.charAt(0).toUpperCase() + dest.slice(1)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`text-lg font-display ${advisor.balanceLeads > 0 ? 'text-navy-900' : 'text-navy-400'}`}>
                      {advisor.balanceLeads}
                    </span>
                    <span className="text-navy-500 text-sm ml-1 font-body">leads</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="font-medium text-navy-900">
                      {advisor.leadsPerWeek === 0 ? '0 (Paused)' : advisor.leadsPerWeek}
                    </span>
                    {advisor.leadsPerWeek !== 0 && <span className="text-navy-500 text-sm ml-1 font-body">/week</span>}
                  </td>
                  <td className="py-4 px-4 text-navy-600 font-body">
                    {advisor.deliveryWeeks} weeks
                  </td>
                  <td className="py-4 px-4">
                    <span className="font-mono text-sm text-navy-600">
                      {advisor.weight.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-navy-600 font-body">
                    {advisor.totalLeadsReceived}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex flex-col gap-1">
                      <span className={`badge ${advisor.isActive ? 'badge-success' : 'badge-error'}`}>
                        {advisor.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {(advisor.isPaused || advisor.leadsPerWeek === 0) && (
                        <span className="badge badge-warning">Paused</span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex justify-end gap-2 flex-wrap">
                      <button
                        onClick={() => openEditSettings(advisor)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        ⚙️Edit
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAdvisor(advisor);
                          setShowAddLeadsModal(true);
                        }}
                        className="text-highlight-600 hover:text-highlight-700 text-sm font-medium"
                      >
                        +Add
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAdvisor(advisor);
                          setShowDeductLeadModal(true);
                        }}
                        disabled={advisor.balanceLeads <= 0}
                        className={`text-sm font-medium ${
                          advisor.balanceLeads > 0
                            ? 'text-coral-600 hover:text-coral-700'
                            : 'text-navy-300 cursor-not-allowed'
                        }`}
                      >
                        −Deduct
                      </button>
                      <button
                        onClick={() => toggleActive(advisor.id)}
                        className={`text-sm font-medium ${
                          advisor.isActive 
                            ? 'text-red-600 hover:text-red-700' 
                            : 'text-emerald-600 hover:text-emerald-700'
                        }`}
                      >
                        {advisor.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {advisors.length === 0 && (
          <div className="text-center py-12 text-navy-500 font-body">
            No advisors registered yet
          </div>
        )}
      </div>

      {/* Edit Settings Modal */}
      {showEditSettingsModal && selectedAdvisor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-luxury-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-xl text-navy-900 mb-2">Edit Settings</h3>
            <p className="text-navy-600 mb-6 font-body">
              Adjust delivery settings for {selectedAdvisor.firstName} {selectedAdvisor.lastName}
            </p>

            <div className="space-y-4 mb-6">
              {/* Leads Per Week Slider */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-navy-700">
                    Leads Per Week
                  </label>
                  <span className="text-lg font-display text-highlight-600">
                    {editSettingsData.leadsPerWeek === 0 ? '0 (Paused)' : editSettingsData.leadsPerWeek}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={editSettingsData.leadsPerWeek}
                  onChange={(e) => setEditSettingsData(prev => ({ 
                    ...prev, 
                    leadsPerWeek: parseInt(e.target.value) 
                  }))}
                  className="w-full h-2 bg-cream-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between mt-1 text-xs text-navy-500">
                  <span>0 (Paused)</span>
                  <span>10/week</span>
                  <span>20/week</span>
                </div>
              </div>

              {/* Requires Phone Checkbox */}
              <div className="p-4 bg-cream-50 rounded-lg border border-cream-200">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editSettingsData.requiresPhone}
                    onChange={(e) => setEditSettingsData(prev => ({ 
                      ...prev, 
                      requiresPhone: e.target.checked 
                    }))}
                    className="w-5 h-5 text-coral-500 rounded focus:ring-coral-500 mt-0.5"
                  />
                  <div className="ml-3">
                    <span className="font-medium text-navy-900">Requires Phone Number</span>
                    <p className="text-sm text-navy-600 font-body mt-1">
                      Only assign leads that have a phone number to this advisor
                    </p>
                  </div>
                </label>
              </div>

              {/* Weekdays Only Checkbox */}
              <div className="p-4 bg-cream-50 rounded-lg border border-cream-200">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editSettingsData.weekdaysOnly}
                    onChange={(e) => setEditSettingsData(prev => ({ 
                      ...prev, 
                      weekdaysOnly: e.target.checked 
                    }))}
                    className="w-5 h-5 text-coral-500 rounded focus:ring-coral-500 mt-0.5"
                  />
                  <div className="ml-3">
                    <span className="font-medium text-navy-900">Weekdays Only</span>
                    <p className="text-sm text-navy-600 font-body mt-1">
                      Only assign leads Monday through Friday. Weekend leads go to other advisors.
                    </p>
                  </div>
                </label>
              </div>

              {/* Daily Priority */}
              <div className="p-4 bg-cream-50 rounded-lg border border-cream-200">
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Daily Priority (leads per day)
                </label>
                <input
                  type="number"
                  min="0"
                  value={editSettingsData.leadsPerDay}
                  onChange={(e) => setEditSettingsData(prev => ({
                    ...prev,
                    leadsPerDay: e.target.value
                  }))}
                  placeholder="Leave blank for normal distribution"
                  className="input w-full text-sm"
                />
                <p className="text-sm text-navy-500 font-body mt-1">
                  If set, this advisor gets prioritized for this many leads each day before other advisors.
                </p>
              </div>

              {/* Notification Email */}
              <div className="p-4 bg-cream-50 rounded-lg border border-cream-200">
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Notification Email Override
                </label>
                <input
                  type="email"
                  value={editSettingsData.notificationEmail}
                  onChange={(e) => setEditSettingsData(prev => ({
                    ...prev,
                    notificationEmail: e.target.value
                  }))}
                  placeholder="Leave blank to use advisor's login email"
                  className="input w-full text-sm"
                />
                <p className="text-sm text-navy-500 font-body mt-1">
                  If set, lead notifications go to this email instead of the advisor's login email.
                </p>
              </div>

              {/* Destination Specialties */}
              <div className="p-4 bg-cream-50 rounded-lg border border-cream-200">
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Destination Specialties
                </label>
                <p className="text-sm text-navy-500 font-body mb-3">
                  Leads from these destinations go to this advisor first. Leave empty for general leads only.
                </p>

                {/* Current destinations */}
                {selectedAdvisor.destinations && selectedAdvisor.destinations.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedAdvisor.destinations.map(dest => (
                      <span key={dest} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {dest.charAt(0).toUpperCase() + dest.slice(1)}
                        <button 
                          onClick={() => removeDestination(dest)}
                          className="ml-1 text-blue-600 hover:text-red-600 font-bold"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Add new destination */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newDestination}
                    onChange={(e) => setNewDestination(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDestination())}
                    placeholder="e.g. greece, japan, thailand"
                    className="input flex-1 text-sm"
                  />
                  <button 
                    onClick={addDestination}
                    disabled={!newDestination.trim()}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Preview */}
              {selectedAdvisor.balanceLeads > 0 && editSettingsData.leadsPerWeek > 0 && (
                <div className="bg-blue-50 rounded p-3 border border-blue-200">
                  <p className="text-sm text-blue-800 font-body">
                    <strong>New timeline:</strong> {selectedAdvisor.balanceLeads} leads at {editSettingsData.leadsPerWeek}/week = {Math.ceil(selectedAdvisor.balanceLeads / editSettingsData.leadsPerWeek)} weeks delivery
                  </p>
                </div>
              )}

              {editSettingsData.leadsPerWeek === 0 && (
                <div className="bg-amber-50 rounded p-3 border border-amber-200">
                  <p className="text-sm text-amber-800 font-body">
                    <strong>Warning:</strong> Setting to 0 will pause lead delivery for this advisor
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditSettingsModal(false);
                  setSelectedAdvisor(null);
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button onClick={updateSettings} className="btn-primary flex-1">
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Leads Modal */}
      {showAddLeadsModal && selectedAdvisor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-luxury-lg max-w-md w-full p-6">
            <h3 className="font-display text-xl text-navy-900 mb-2">Add Leads</h3>
            <p className="text-navy-600 mb-6 font-body">
              Manually add leads to {selectedAdvisor.firstName} {selectedAdvisor.lastName}'s account
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Number of Leads
                </label>
                <input
                  type="number"
                  min="1"
                  value={addLeadsData.leads}
                  onChange={(e) => setAddLeadsData(prev => ({ ...prev, leads: parseInt(e.target.value) || 1 }))}
                  className="input"
                />
              </div>

              <div className="bg-cream-50 rounded p-3">
                <p className="text-sm text-navy-600 font-body">
                  New balance will be: <span className="font-mono font-medium">
                    {selectedAdvisor.balanceLeads + addLeadsData.leads} leads
                  </span>
                </p>
                <p className="text-sm text-navy-500 font-body mt-1">
                  Delivery pace stays at {selectedAdvisor.leadsPerWeek || 2}/week. Use Edit to change it.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddLeadsModal(false);
                  setSelectedAdvisor(null);
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button onClick={addLeads} className="btn-primary flex-1">
                Add Leads
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deduct Lead Modal */}
      {showDeductLeadModal && selectedAdvisor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-luxury-lg max-w-md w-full p-6">
            <h3 className="font-display text-xl text-navy-900 mb-2">Deduct Lead</h3>
            <p className="text-navy-600 mb-6 font-body">
              Deduct 1 lead from {selectedAdvisor.firstName} {selectedAdvisor.lastName}'s balance
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm font-body">
                  Current balance: <strong>{selectedAdvisor.balanceLeads} leads</strong><br />
                  After deduction: <strong>{selectedAdvisor.balanceLeads - 1} leads</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={deductReason}
                  onChange={(e) => setDeductReason(e.target.value)}
                  placeholder="e.g., Phone call lead sent manually"
                  className="input"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeductLeadModal(false);
                  setSelectedAdvisor(null);
                  setDeductReason('');
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button 
                onClick={deductLead} 
                className="flex-1 py-3 bg-coral-500 hover:bg-coral-600 text-white font-medium rounded transition-colors"
              >
                Deduct Lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
